import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TimeSpan } from '../hours-of-operation.interface';
import { UntypedFormControl } from '@angular/forms';
import { TimePicker } from '../../time-picker/time-picker.component';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

@Component({
  selector: 'forms-hoo-time-span',
  templateUrl: './time-span-edit.component.html',
  styleUrls: ['./time-span-edit.component.scss'],
  standalone: false,
})
export class HoursOfOperationTimeSpanEditComponent implements OnInit {
  @Input() disableTimeSelectors = false;
  @Input() allowDelete = false;
  @Input() isHolidayHours = false;
  @Output() delete = new EventEmitter();

  startTime: UntypedFormControl = new UntypedFormControl();
  endTime: UntypedFormControl = new UntypedFormControl();
  private _value: TimeSpan;
  errorMessage = '';

  @Input() get value(): TimeSpan {
    return this._value;
  }
  set value(val: TimeSpan) {
    this._value = val;
    this.valueChange.emit(this._value);
  }

  @Output() valueChange = new EventEmitter();

  @Output() hasAnyErrors = new EventEmitter();
  startTimeHasErrors = false;
  endTimeHasErrors = false;

  get allDay(): boolean {
    return this.startTime.value === TimePicker.ALL_DAY;
  }

  get notesOnly(): boolean {
    return this.startTime.value === TimePicker.NOTES_ONLY;
  }

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    if (!this.value) {
      return;
    }

    // remove trailing :SS for times like 09:00:00, as this causes a validation error
    if (this.value.opens) {
      this.value.opens = this.value.opens.split(':', 2).join(':');
    }
    if (this.value.closes) {
      this.value.closes = this.value.closes.split(':', 2).join(':');
    }

    if (this.value.opens === '0:00' && this.value.closes === '0:00') {
      this.startTime.setValue(TimePicker.ALL_DAY);
    } else if (typeof this.value.opens === 'undefined' && typeof this.value.closes === 'undefined') {
      this.startTime.setValue(TimePicker.NOTES_ONLY);
    } else {
      this.startTime.setValue(this.value.opens);
      this.endTime.setValue(this.value.closes);
    }

    this.startTime.valueChanges.subscribe((val) => {
      if (val === TimePicker.ALL_DAY) {
        this.value.opens = '0:00';
        this.value.closes = '0:00';
      } else if (val === TimePicker.NOTES_ONLY) {
        this.value.opens = undefined;
        this.value.closes = undefined;
      } else {
        this.value.opens = val;
        if (!this.endTime.value) {
          this.endTime.setValue('17:00');
        }
      }
    });
    this.endTime.valueChanges.subscribe((val) => {
      if (!this.allDay && !this.notesOnly) {
        this.value.closes = val === '0:00' ? '24:00' : val;
      }
    });
    if (this.disableTimeSelectors) {
      this.startTime.setValue(TimePicker.NOTES_ONLY);
    }
  }

  deleteCallback(): void {
    this.delete.emit();
  }

  blur(control: UntypedFormControl): void {
    if (!control.value) {
      // console.log(control.value);
      // control.setValue('24:00');
    }
  }

  handleHasAnyErrors(errStartTime, errEndTime): void {
    if (this.startTimeHasErrors !== errStartTime || this.endTimeHasErrors !== errEndTime) {
      this.startTimeHasErrors = errStartTime;
      this.endTimeHasErrors = errEndTime;
    }
    if (this.allDay || (this.notesOnly && this.isHolidayHours)) {
      this.hasAnyErrors.emit(this.startTimeHasErrors || this.endTimeHasErrors);
      return;
    }

    // mark an error if
    // - hours are empty
    // - closing time is before opening time
    const timeIsEmpty = !this.value.opens || !this.value.closes;

    if (this.notesOnly && !this.isHolidayHours) {
      this.errorMessage = this.translateService.instant('FRONTEND.FORMS.HOURS_OF_OPERATION.NOTES_ONLY_IS_DEPRECATED');
    } else if (timeIsEmpty) {
      this.errorMessage = this.translateService.instant('FRONTEND.FORMS.HOURS_OF_OPERATION.HOURS_CANNOT_BE_EMPTY');
    } else {
      this.errorMessage = '';
    }

    this.hasAnyErrors.emit(
      (this.notesOnly && !this.isHolidayHours) || timeIsEmpty || this.startTimeHasErrors || this.endTimeHasErrors,
    );
  }

  private isOpeningBeforeClosing(closeTime: dayjs.Dayjs, openTime: dayjs.Dayjs): boolean {
    // Can only uplift from openTime/closeTime hours to All Day if set to 12:00AM and 12:00AM
    if (closeTime.hour() === 0 && closeTime.minute() === 0) {
      return true;
    }
    // If closing is before opening
    return openTime.isBefore(closeTime);
  }

  convertTimeToDayJs(time: string): dayjs.Dayjs {
    if (time === '') return null;

    time = time.toLowerCase().replace(/ /g, '');
    if (time.includes('am') || time.includes('pm')) {
      return dayjs(time, 'hh:mma');
    }
    return dayjs(time, 'HH:mm');
  }
}
